import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, IconButton, Paper, Typography } from "@mui/material";
import KeyboardDoubleArrowDownIcon from "@mui/icons-material/KeyboardDoubleArrowDown";
import KeyboardDoubleArrowUpIcon from "@mui/icons-material/KeyboardDoubleArrowUp";
import { ChevronDown, ChevronRight, Eye, Info, Sparkles, TrendingDown, TrendingUp } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import CommonTable from "../../../common_components/CommonTable";
import TableToolbar from "../../../common_components/TableToolbar";
import CommonLoader from "../../../common_components/CommonLoader";
import VarianceBadge from "../../../common_components/VarianceBadge";
import AppButton from "../../../common_components/AppButton";
import HoverTooltip from "../../../common_components/hoverOverlay/HoverTooltip";
import { toast } from "../../../common_components/Toast/ToastUtils";
import { formatValue, percentChange } from "../reportBuilder.utils";
import { GroupIcon } from "./builderIcons";
import { StyledCurrency } from "../../../helper/StyledCurrency";
import { fetchAdPreview, fetchAdApprovalAccounts, selectClientAdApprovalAccounts, selectClientAdApprovalPreviewOptions, } from "../../../redux/clientAdApprovalSlice";
import AdPreview from "../../ClientAdApproval/components/AdPreview";
/** API network key → builder key ("ga" → "google_analytics"), for the icon. */
const toBuilderNetworkKey = (apiKey) => (apiKey === "ga" ? "google_analytics" : apiKey);
/** "2026-09-19" → "09/19/2026", as the rest of the portal shows a period. */
const usDate = (iso) => {
    const [y, m, d] = String(iso || "").split("-");
    return y && m && d ? `${m}/${d}/${y}` : "";
};
/** Accent colours for the totals tiles — same palette as the portal charts. */
const CHART_PALETTE = [
    { color: "#8C1D5B", compare: "#F9A8D4" },
    { color: "#5F8F6B", compare: "#6EE7B7" },
    { color: "#7A5BB6", compare: "#A5B4FC" },
    { color: "#D19A3A", compare: "#E7C37A" },
    { color: "#5A95D6", compare: "#9FC3EA" },
    { color: "#6FAFB0", compare: "#A8D8D9" },
    { color: "#4B556B", compare: "#9CA3AF" },
];
const MAX_SUMMARY_TILES = 6;
/** Pinned label column widths — see the column builder for why they are fixed. */
const PINNED_WIDTH = 200;
/** A week reads "Sep 21, 2026 - Sep 27, 2026". */
const PINNED_WEEK_WIDTH = 240;
/** Tree layout — Day / Month: "Sep 24, 2026" plus the chevron. */
const PINNED_DATE_WIDTH = 140;
/** Tree layout — Campaign / Ad set / Ad name columns. */
const PINNED_NAME_WIDTH = 160;
const DEFAULT_PREVIEW_FORMAT_OPTIONS = [
    { value: "MOBILE_FEED_STANDARD", label: "Mobile Feed Standard" },
    { value: "DESKTOP_FEED_STANDARD", label: "Desktop Feed Standard" },
    { value: "INSTANT_ARTICLE_STANDARD", label: "Instant Article Standard" },
    { value: "INSTREAM_VIDEO_MOBILE", label: "Instream Video Mobile" },
    { value: "INSTREAM_BANNER_MOBILE", label: "Instream Banner Mobile" },
    { value: "INSTREAM_VIDEO_DESKTOP", label: "Instream Video Desktop" },
    { value: "INSTREAM_BANNER_DESKTOP1", label: "Instream Banner Desktop" },
    { value: "RIGHT_COLUMN_STANDARD", label: "Right Column Standard" },
    { value: "WATCH_FEED_MOBILE", label: "Watch Feed Mobile" },
    { value: "SUGGESTED_VIDEO_MOBILE", label: "Suggested Video Mobile" },
    { value: "MARKETPLACE_MOBILE", label: "Marketplace Mobile" },
    { value: "INSTAGRAM_STORY", label: "Instagram Story" },
    { value: "INSTAGRAM_STANDARD", label: "Instagram Standard" },
    { value: "FACEBOOK_STORY_MOBILE", label: "Facebook Story Mobile" },
    { value: "INSTAGRAM_EXPLORE_CONTEXTUAL", label: "Instagram Explore Contextual" },
    { value: "INSTAGRAM_EXPLORE_IMMERSIVE", label: "Instagram Explore Immersive" },
    { value: "MOBILE_INTERSTITIAL", label: "Mobile Interstitial" },
    { value: "MOBILE_NATIVE", label: "Mobile Native" },
    { value: "MOBILE_BANNER", label: "Mobile Banner" },
    { value: "AUDIENCE_NETWORK_INSTREAM_VIDEO", label: "Audience Network Instream Video" },
    { value: "AUDIENCE_NETWORK_REWARDED_VIDEO", label: "Audience Network Rewarded Video" },
    { value: "MOBILE_MEDIUM_RECTANGLE", label: "Mobile Medium Rectangle" },
    { value: "MESSENGER_MOBILE_STORY_MEDIA", label: "Messenger Mobile Story Media" },
];
// ── Query summary block ──────────────────────────────────────────────────
const KEYWORDS = new Set([
    "FROM", "SHOW", "BY", "WHERE", "SINCE", "UNTIL", "COMPARE", "TO",
    "ORDER", "AND", "OR", "IN", "NOT", "LIKE", "DESC", "ASC",
]);
/** Colour one query line: keywords blue, strings teal, numbers amber. */
const renderQueryLine = (line) => line.split(/(\s+|,)/).map((token, idx) => {
    if (!token.trim())
        return _jsx("span", { children: token }, idx);
    const bare = token.replace(/[(),]/g, "");
    let color = "#101828";
    if (KEYWORDS.has(bare.toUpperCase()) && bare === bare.toUpperCase()) {
        color = "#1A73E8";
    }
    else if (/^'.*'$/.test(bare) || token.startsWith("'")) {
        color = "#0E9384";
    }
    else if (/^[\d.]+$/.test(bare)) {
        color = "#B54708";
    }
    else if (/^[a-z_][a-z0-9_.]*$/.test(bare)) {
        color = "#7A5BB6";
    }
    return (_jsx("span", { style: { color }, children: token }, idx));
});
const QueryBlock = ({ summary, text }) => {
    const [open, setOpen] = useState(false);
    const lines = (text || "").split("\n");
    return (_jsxs(Box, { sx: {
            border: "1px solid #E4E7EC",
            borderRadius: "8px",
            bgcolor: "#fff",
            overflow: "hidden",
        }, children: [_jsxs(Box, { role: "button", tabIndex: 0, onClick: () => setOpen((prev) => !prev), onKeyDown: (e) => {
                    if (e.key === "Enter" || e.key === " ")
                        setOpen((prev) => !prev);
                }, sx: {
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    px: 1.5,
                    py: 1,
                    cursor: "pointer",
                    "&:hover": { bgcolor: "#FCFCFD" },
                }, children: [_jsx(Box, { sx: {
                            width: 8,
                            height: 8,
                            borderRadius: "50%",
                            flex: "0 0 auto",
                            bgcolor: summary.isValid ? "#12B76A" : "#F79009",
                        } }), _jsx(Typography, { className: "all-Font-Sizes", sx: { flex: 1, color: "#344054", fontWeight: 500 }, children: summary.message }), _jsx(Typography, { className: "all-Font-Sizes-12", sx: { color: "#98A2B3" }, children: open ? "Hide query" : "Show query" }), _jsx(Box, { sx: {
                            display: "flex",
                            color: "#98A2B3",
                            transform: open ? "rotate(180deg)" : "none",
                            transition: "transform .15s ease",
                        }, children: _jsx(ChevronDown, { size: 16 }) })] }), open && summary.isValid && (_jsx(Box, { sx: {
                    borderTop: "1px solid #F2F4F7",
                    bgcolor: "#FCFCFD",
                    px: 1.5,
                    py: 1.25,
                    overflowX: "auto",
                }, children: lines.map((line, idx) => (_jsxs(Box, { sx: { display: "flex", gap: 1.5 }, children: [_jsx(Typography, { sx: {
                                fontFamily: "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace",
                                fontSize: "var(--font-size-body)",
                                lineHeight: 1.8,
                                color: "#D0D5DD",
                                userSelect: "none",
                                minWidth: 14,
                                textAlign: "right",
                            }, children: idx + 1 }), _jsx(Typography, { component: "pre", sx: {
                                m: 0,
                                fontFamily: "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace",
                                fontSize: "var(--font-size-body)",
                                lineHeight: 1.8,
                                whiteSpace: "pre",
                            }, children: renderQueryLine(line) })] }, idx))) }))] }));
};
// ── Summary tile ─────────────────────────────────────────────────────────
const SummaryTile = ({ label, value, change, accent }) => {
    const up = change != null && change >= 0;
    // Whole percent; one that rounds to nothing reads as a flat "0%".
    const rounded = change == null ? null : Math.round(change);
    const flat = rounded === 0;
    return (_jsxs(Box, { sx: {
            flex: "1 1 168px",
            minWidth: 168,
            px: 1.75,
            py: 1.5,
            borderRadius: "8px",
            border: "1px solid #E4E7EC",
            bgcolor: "#fff",
        }, children: [_jsxs(Box, { sx: { display: "flex", alignItems: "center", gap: 0.75, mb: 0.5 }, children: [_jsx(Box, { sx: { width: 8, height: 8, borderRadius: "50%", bgcolor: accent, flex: "0 0 auto" } }), _jsx(Typography, { className: "all-Font-Sizes-14", sx: {
                            color: "#475467",
                            fontWeight: 500,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                        }, title: label, children: label })] }), _jsx(StyledCurrency, { value: value, fontSize: "20px", symbolSize: "20px", valueColor: "#101828", symbolColor: "#9CA3AF", valueWeight: 700, symbolWeight: 700, style: { display: "block", lineHeight: "28px", whiteSpace: "nowrap" } }), change != null && (_jsxs(Box, { sx: { display: "flex", alignItems: "center", gap: 0.375, mt: 0.25 }, children: [!flat && (_jsx(Box, { sx: { display: "flex", color: up ? "#027A48" : "#B42318" }, children: up ? _jsx(TrendingUp, { size: 13 }) : _jsx(TrendingDown, { size: 13 }) })), _jsx(Typography, { className: "all-Font-Sizes-12", sx: { color: flat ? "#B54708" : up ? "#027A48" : "#B42318", fontWeight: 600 }, children: flat ? "0%" : `${up ? "+" : ""}${rounded.toLocaleString("en-US")}%` }), _jsx(Typography, { className: "all-Font-Sizes-12", sx: { color: "#98A2B3" }, children: "vs compare" })] }))] }));
};
// ── Comparison cell ──────────────────────────────────────────────────────
/**
 * One metric cell in the data table: the period value, the compare-range value
 * under it, and the shared change pill beside them.
 *
 * The stacked-value + `VarianceBadge` pill layout is the portal's standard for
 * comparison tables (Account Performance, Performance Tracker, Commission
 * Tracker), so this uses the shared badge rather than a local delta caption.
 */
const ComparisonCell = ({ value, compare, change, bold = false, valueColor = "#181D27" }) => (_jsxs(Box, { sx: {
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-start",
        gap: 0.75,
    }, children: [_jsxs(Box, { sx: { display: "flex", flexDirection: "column", alignItems: "flex-start" }, children: [_jsx(StyledCurrency, { value: value, className: "all-Font-Sizes", fontSize: "14px", symbolSize: "14px", valueColor: valueColor, symbolColor: "#9CA3AF", valueWeight: bold ? 600 : 400, symbolWeight: bold ? 600 : 400, style: { lineHeight: 1.5, whiteSpace: "nowrap" } }), compare != null && (_jsx(Box, { sx: { mt: "2px" }, children: _jsx(StyledCurrency, { value: compare, className: "all-Font-Sizes-12", fontSize: "13px", symbolSize: "13px", valueColor: "#74777d", symbolColor: "#9CA3AF", valueWeight: 400, symbolWeight: 400, style: { lineHeight: 1.5, whiteSpace: "nowrap" } }) }))] }), change != null && (_jsx(VarianceBadge, { value: change, 
            // Whole percent. A change that rounds to nothing (0 vs 0 too) is a
            // flat "0%" pill, no up / down arrow.
            label: `${Math.abs(Math.round(change)).toLocaleString("en-US")}%`, neutral: Math.round(change) === 0, bold: bold, fontSize: "13px" }))] }));
// ── Empty state ──────────────────────────────────────────────────────────
const EmptyState = ({ title, body, action }) => (_jsxs(Box, { sx: {
        minHeight: 320,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        textAlign: "center",
        px: 3,
        py: 6,
    }, children: [_jsxs(Box, { sx: {
                width: 112,
                height: 128,
                borderRadius: "10px",
                border: "1px solid #EAECF0",
                bgcolor: "#FCFCFD",
                p: 1.5,
                display: "flex",
                flexDirection: "column",
                gap: 1,
                mb: 2.5,
            }, children: [_jsx(Box, { sx: { display: "flex", alignItems: "flex-end", gap: 0.75, height: 44 }, children: [0.45, 0.75, 0.55, 1, 0.7].map((h, i) => (_jsx(Box, { sx: {
                            flex: 1,
                            height: `${h * 100}%`,
                            borderRadius: "2px",
                            bgcolor: i === 3 ? "#FFC8E9" : "#EAECF0",
                        } }, i))) }), [0, 1, 2, 3].map((row) => (_jsx(Box, { sx: { display: "flex", gap: 0.5 }, children: [0, 1, 2].map((cell) => (_jsx(Box, { sx: { flex: 1, height: 6, borderRadius: "2px", bgcolor: "#EAECF0" } }, cell))) }, row)))] }), _jsx(Typography, { className: "head-Font-Sizes", sx: { fontWeight: 600, color: "#344054", mb: 0.5 }, children: title }), _jsx(Typography, { className: "all-Font-Sizes", sx: { color: "#667085", maxWidth: 420 }, children: body }), action && _jsx(Box, { sx: { mt: 2.5 }, children: action })] }));
// ── Main ─────────────────────────────────────────────────────────────────
const ReportPreview = ({ state, report, loading, stale, querySummary, onGenerate, disabled = false, clientName = "", clientId, title, onEditCustomMetric, onAskAi, }) => {
    const dispatch = useDispatch();
    const reduxPreviewOptions = useSelector(selectClientAdApprovalPreviewOptions);
    const accountOptions = useSelector(selectClientAdApprovalAccounts);
    // Fetch accounts when clientId changes
    const lastFetchedClientId = useRef(null);
    useEffect(() => {
        if (clientId && lastFetchedClientId.current !== clientId) {
            lastFetchedClientId.current = clientId;
            dispatch(fetchAdApprovalAccounts({ clientId }));
        }
    }, [clientId, dispatch]);
    const previewFormatOptions = useMemo(() => {
        if (Array.isArray(reduxPreviewOptions) && reduxPreviewOptions.length > 0) {
            return reduxPreviewOptions;
        }
        return DEFAULT_PREVIEW_FORMAT_OPTIONS;
    }, [reduxPreviewOptions]);
    const [previewOpen, setPreviewOpen] = useState(false);
    const [previewRow, setPreviewRow] = useState(null);
    const [previewFormat, setPreviewFormat] = useState("MOBILE_FEED_STANDARD");
    const [previewData, setPreviewData] = useState({
        loading: false,
        error: null,
        html: null,
        url: null,
    });
    const fetchPreview = useCallback(async (targetRow, format) => {
        const adId = targetRow?.adId || targetRow?.ad_id;
        if (!adId) {
            setPreviewData({
                loading: false,
                html: null,
                url: null,
                error: "Preview unavailable. This ad does not have a valid Ad ID.",
            });
            toast.error("Preview unavailable. This ad does not have a valid Ad ID.");
            return;
        }
        let accId = targetRow?.account_id || targetRow?.accountId;
        if (!accId && accountOptions?.length) {
            const matched = accountOptions.find((o) => o.value === targetRow?.connection_id);
            accId = matched?.accountId || matched?.value || accountOptions[0]?.accountId || accountOptions[0]?.value;
        }
        setPreviewData({ loading: true, error: null, html: null, url: null });
        try {
            const result = await dispatch(fetchAdPreview({
                adId: String(adId),
                format,
                accountId: accId ? String(accId) : undefined,
                toastOnError: false,
            })).unwrap();
            setPreviewData({
                loading: false,
                html: result?.html || null,
                url: result?.previewUrl || null,
                error: !result?.html && !result?.previewUrl ? "Preview unavailable for this format" : null,
            });
        }
        catch (e) {
            const errorMsg = typeof e === "string" ? e : (e?.message || "Error fetching ad preview");
            setPreviewData({
                loading: false,
                html: null,
                url: null,
                error: errorMsg,
            });
        }
    }, [dispatch, accountOptions]);
    const handlePreviewOpen = useCallback((row, name) => {
        const adName = name || row.dimensions?.lvl_ad_name || row.dimensions?.bd_ad_name || row.dimensions?.ad_name || "";
        const adId = row.ad_id || row.adId;
        const targetRow = {
            ad_name: adName,
            adId: adId || null,
            account_id: row.account_id || row.accountId || null,
            connection_id: row.connection_id || null,
        };
        setPreviewRow(targetRow);
        setPreviewOpen(true);
        const fmt = previewFormat || "MOBILE_FEED_STANDARD";
        fetchPreview(targetRow, fmt);
    }, [previewFormat, fetchPreview]);
    const handlePreviewFormatChange = useCallback((fmt) => {
        setPreviewFormat(fmt);
        if (previewRow) {
            fetchPreview(previewRow, fmt);
        }
    }, [previewRow, fetchPreview]);
    const handleClosePreview = useCallback(() => {
        setPreviewOpen(false);
        setPreviewRow(null);
        setPreviewData({ loading: false, error: null, html: null, url: null });
    }, []);
    const [sortConfig, setSortConfig] = useState(null);
    // Tree layout: which nodes are open. A new report opens fully expanded —
    // every day, campaign and ad set — so nothing is hidden at first.
    const [expanded, setExpanded] = useState(new Set());
    // Every node that has children - what "expand all" opens.
    const parentIds = useMemo(() => {
        const ids = [];
        const walk = (list) => list.forEach((node) => {
            if (node.children?.length) {
                ids.push(node.id);
                walk(node.children);
            }
        });
        if (report?.grouped)
            walk(report.rows);
        return ids;
    }, [report]);
    useEffect(() => {
        setExpanded(new Set(parentIds));
    }, [parentIds]);
    const toggle = (id) => setExpanded((prev) => {
        const next = new Set(prev);
        if (next.has(id))
            next.delete(id);
        else
            next.add(id);
        return next;
    });
    // Header toggle: anything still open -> collapse all, else expand all.
    const anyExpanded = parentIds.some((id) => expanded.has(id));
    const toggleAll = useCallback(() => setExpanded(anyExpanded ? new Set() : new Set(parentIds)), [anyExpanded, parentIds]);
    // ── Table columns ────────────────────────────────────────────────────
    const columns = useMemo(() => {
        if (!report)
            return [];
        const cols = [];
        report.columns.forEach((column) => {
            if (!column.is_dimension) {
                // A custom metric's header: name + pencil. The pencil's tooltip
                // is the description (when there is one); on Edit it opens the
                // metric's dialog.
                const pencil = (_jsx(Box, { component: "span", role: onEditCustomMetric ? "button" : undefined, "aria-label": `Edit ${column.label}`, onClick: (e) => {
                        // Not a sort click.
                        e.stopPropagation();
                        // onEditCustomMetric?.(column.id);
                    }, sx: {
                        display: "inline-flex",
                        color: "#98A2B3",
                        cursor: onEditCustomMetric ? "pointer" : "default",
                        "&:hover": onEditCustomMetric ? { color: "#FF008E" } : undefined,
                    }, children: _jsx(Info, { size: 13 }) }));
                const label = column.is_custom ? (_jsxs(Box, { component: "span", sx: { display: "inline-flex", alignItems: "center", gap: 0.5 }, children: [column.label, column.description ? (_jsx(HoverTooltip, { title: column.description, placement: "top", children: pencil })) : (pencil)] })) : (column.label);
                cols.push({
                    id: column.id,
                    label,
                    width: "150px",
                    minWidth: 130,
                    align: "left",
                    sortable: true,
                });
                return;
            }
            // Label columns are pinned, and CommonTable places each pinned
            // column at the SUM of the declared widths before it. So a pinned
            // cell must be exactly that wide — if a long label (a week range, a
            // campaign name) let it grow, the next pinned column would sit on
            // top of it and the scrolling columns would show underneath. The
            // width is locked here and long text is cut with an ellipsis.
            // The tree pins one column per level (up to four), so they are kept
            // narrow enough to leave the metrics room: a date needs little, a
            // name is cut with an ellipsis (hover shows it in full).
            const level = column.id.replace("__dimension__", "").replace(/^lvl_/, "");
            const isCampaignCol = column.id === "__dimension__campaign" || column.id.includes("campaign");
            const isSendTimeCol = column.id === "__dimension__send_time" || column.id.includes("send_time");
            const isWeekdayCol = column.id === "__dimension__weekday" || column.id.includes("weekday");
            const isAdCol = column.id.includes("ad_name");
            const isPublisherCol = column.id.includes("publisher");
            const isMultiLineCol = isAdCol || isCampaignCol || isPublisherCol;
            const width = report.grouped
                ? level === "week"
                    ? PINNED_WEEK_WIDTH
                    : level === "day" || level === "month"
                        ? PINNED_DATE_WIDTH
                        : PINNED_NAME_WIDTH
                : level === "week"
                    ? PINNED_WEEK_WIDTH
                    : isSendTimeCol
                        ? 180
                        : isWeekdayCol
                            ? 130
                            : isCampaignCol || isPublisherCol
                                ? 240
                                : PINNED_WIDTH;
            const headerLocked = {
                width,
                minWidth: width,
                maxWidth: width,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                boxSizing: "border-box",
            };
            const cellLocked = {
                width,
                minWidth: width,
                maxWidth: width,
                overflow: "hidden",
                textOverflow: "ellipsis",
                boxSizing: "border-box",
                ...(isMultiLineCol ? {} : { whiteSpace: "nowrap" }),
            };
            // Tree layout: the first label column's header carries the
            // expand / collapse all toggle, as the roles and permissions drawer does.
            const isFirstDimension = report.columns.find((c) => c.is_dimension)?.id === column.id;
            const label = report.grouped && isFirstDimension && parentIds.length > 0 ? (_jsxs(Box, { component: "span", sx: { display: "inline-flex", alignItems: "center", gap: 0.5 }, children: [_jsx(HoverTooltip, { title: anyExpanded ? "Collapse all" : "Expand all", placement: "top", children: _jsx(IconButton, { size: "small", "aria-label": anyExpanded ? "Collapse all" : "Expand all", onClick: (e) => {
                                e.stopPropagation();
                                toggleAll();
                            }, sx: { p: 0 }, children: anyExpanded ? (_jsx(KeyboardDoubleArrowDownIcon, { sx: { fontSize: 18, color: "#181D27" } })) : (_jsx(KeyboardDoubleArrowUpIcon, { sx: { fontSize: 18, color: "#181D27" } })) }) }), column.label] })) : (column.label);
            cols.push({
                id: column.id,
                label,
                width: `${width}px`,
                minWidth: width,
                align: "left",
                sortable: !report.grouped,
                headerSx: headerLocked,
                cellSx: () => cellLocked,
            });
        });
        return cols;
    }, [report, onEditCustomMetric, parentIds, anyExpanded, toggleAll]);
    // ── Table rows (sorted locally) ──────────────────────────────────────
    // Sorting orders each level among its siblings; a node's children always
    // stay directly under it.
    const rows = useMemo(() => {
        if (!report)
            return [];
        const sortList = (list) => {
            if (!sortConfig)
                return list;
            const { columnId, direction } = sortConfig;
            return [...list].sort((a, b) => {
                if (columnId.startsWith("__dimension__")) {
                    const dimKey = columnId.replace("__dimension__", "");
                    const av = String(a.dimensions[dimKey] ?? "");
                    const bv = String(b.dimensions[dimKey] ?? "");
                    return direction === "asc"
                        ? av.localeCompare(bv)
                        : bv.localeCompare(av);
                }
                const av = a.values[columnId] ?? 0;
                const bv = b.values[columnId] ?? 0;
                return direction === "asc" ? av - bv : bv - av;
            });
        };
        if (!report.grouped) {
            return sortList(report.rows).map((row) => ({ ...row, level: "flat" }));
        }
        // Depth-first: a node, then (if open) its children, and so on down.
        const flatten = (list) => sortList(list).flatMap((node) => {
            const hasChildren = Boolean(node.children?.length);
            return [
                { ...node, level: hasChildren ? "parent" : "child" },
                ...(hasChildren && expanded.has(node.id) ? flatten(node.children) : []),
            ];
        });
        return flatten(report.rows);
    }, [report, sortConfig, expanded]);
    const renderCell = (row, column) => {
        const definition = report?.columns.find((c) => c.id === column.id);
        if (!definition)
            return null;
        // ── Tree: one column per level (Day | Campaign | Ad set | Ad) ────
        // A node's name sits in its own level's column; the ancestors' cells
        // are blank (they are the rows above) and the finer ones read "All".
        if (definition.is_dimension && row.level !== "flat") {
            const depth = row.depth || 1;
            const dimensionId = column.id.replace("__dimension__", "");
            const text = row.dimensions[dimensionId] ?? "";
            const isOwnCell = report?.columns.filter((c) => c.is_dimension)[depth - 1]?.id === column.id;
            if (!isOwnCell) {
                if (!text)
                    return null;
                return (_jsx(Typography, { className: "all-Font-Sizes", sx: { color: "#667085", whiteSpace: "nowrap" }, children: text }));
            }
            const isParent = row.level === "parent";
            const open = expanded.has(row.id);
            // A comparison-only row already shows the comparison day as its label.
            const compareLabel = row.compare_only ? undefined : row.compare_dimensions?.[dimensionId];
            const isAdNameLevel = dimensionId === "lvl_ad_name" || dimensionId === "bd_ad_name" || dimensionId === "ad_name";
            const isCampaignLevel = dimensionId === "lvl_campaign" || dimensionId === "campaign";
            const treeStatus = isCampaignLevel ? (row.status || row.dimensions.status) : undefined;
            return (_jsxs(Box, { role: isParent ? "button" : undefined, tabIndex: isParent ? 0 : undefined, onClick: isParent ? () => toggle(row.id) : undefined, onKeyDown: isParent
                    ? (e) => {
                        if (e.key === "Enter" || e.key === " ")
                            toggle(row.id);
                    }
                    : undefined, sx: {
                    display: "flex",
                    alignItems: isAdNameLevel ? "flex-start" : "center",
                    gap: 0.5,
                    cursor: isParent ? "pointer" : "default",
                    minWidth: 0,
                    width: "100%",
                    py: isAdNameLevel ? 0.25 : 0,
                }, children: [isParent && (_jsx(Box, { sx: { flex: "0 0 auto", display: "flex", color: "#667085", mt: isAdNameLevel ? "2px" : 0 }, children: open ? _jsx(ChevronDown, { size: 14 }) : _jsx(ChevronRight, { size: 14 }) })), _jsxs(Box, { sx: { minWidth: 0, flex: 1, display: "flex", flexDirection: "column", alignItems: "flex-start", width: "100%" }, children: [_jsx(HoverTooltip, { title: text || "", placement: "top", children: _jsx(Typography, { className: "all-Font-Sizes", sx: {
                                        // The day dark, every name under it in pink —
                                        // the reference's link colour.
                                        color: depth === 1 ? "#101828" : "#C11574",
                                        fontWeight: depth === 1 ? 600 : 400,
                                        whiteSpace: "nowrap",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        width: "100%",
                                    }, children: text || "-" }) }), isCampaignLevel && treeStatus && (_jsx(HoverTooltip, { title: `Status: ${treeStatus}`, placement: "top", children: _jsxs(Typography, { sx: {
                                        fontSize: "12px !important",
                                        color: "#101828",
                                        fontWeight: 500,
                                        whiteSpace: "nowrap",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        width: "100%",
                                        mt: "2px",
                                        textTransform: "capitalize"
                                    }, children: ["Status: ", treeStatus] }) })), isAdNameLevel && text && text !== "All" && text !== "-" && (_jsxs(Box, { component: "button", type: "button", onClick: (e) => {
                                    e.stopPropagation();
                                    handlePreviewOpen(row, text);
                                }, sx: {
                                    border: "none",
                                    backgroundColor: "transparent",
                                    cursor: "pointer",
                                    p: 0,
                                    mt: 0.25,
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: "4px",
                                    color: "inherit",
                                    lineHeight: 1,
                                    width: "fit-content",
                                    "&:hover": {
                                        opacity: 0.75,
                                        "& .preview-label": { textDecoration: "underline" },
                                    },
                                }, children: [_jsx(Eye, { size: 13, style: { flexShrink: 0 } }), _jsx(Typography, { className: "preview-label", sx: {
                                            fontSize: "12px !important",
                                            fontWeight: 500,
                                            color: "inherit",
                                            lineHeight: 1,
                                        }, children: "Preview" })] })), compareLabel && (_jsx(Typography, { sx: {
                                    fontSize: "var(--font-size-small) !important",
                                    color: "#667085",
                                    fontWeight: 500,
                                    whiteSpace: "nowrap",
                                    mt: 0.25,
                                }, children: compareLabel }))] })] }));
        }
        // Leaves read quieter than their parents: the value alone, "-" where
        // the row's network has no such column.
        if (!definition.is_dimension && row.level === "child") {
            // A comparison-only row has no primary figure.
            const value = row.compare_only ? undefined : row.values[column.id];
            const childCompare = row.compare_values?.[column.id];
            // Compare on: the period value, the compare value under it and the
            // change pill, like the day row above - just in the quieter colour.
            if (childCompare != null) {
                return (_jsx(ComparisonCell, { value: value == null ? "-" : formatValue(value, definition.format), compare: formatValue(childCompare, definition.format), change: value != null ? percentChange(value, childCompare) : null, valueColor: "#475467" }));
            }
            return (_jsx(Box, { sx: { textAlign: "left", whiteSpace: "nowrap" }, children: _jsx(StyledCurrency, { value: value == null ? "-" : formatValue(value, definition.format), className: "all-Font-Sizes", fontSize: "14px", symbolSize: "14px", valueColor: value == null ? "#98A2B3" : "#475467", symbolColor: "#9CA3AF", valueWeight: 400, symbolWeight: 400, style: { lineHeight: 1.5 } }) }));
        }
        if (definition.is_dimension) {
            const dimensionId = column.id.replace("__dimension__", "");
            const label = row.dimensions[dimensionId] || "-";
            // Only time dimensions carry one — the day / week / month the
            // compare figures on this row came from.
            // A comparison-only row already shows the comparison day as its label.
            const compareLabel = row.compare_only ? undefined : row.compare_dimensions?.[dimensionId];
            const isAdNameLevel = dimensionId === "lvl_ad_name" || dimensionId === "bd_ad_name" || dimensionId === "ad_name";
            const isCampaignCol = dimensionId === "campaign" || dimensionId === "lvl_campaign";
            const status = isCampaignCol ? (row.status || row.dimensions.status) : undefined;
            return (_jsxs(Box, { sx: { minWidth: 0, display: "flex", flexDirection: "column", alignItems: "flex-start", width: "100%", py: isAdNameLevel || (isCampaignCol && status) ? 0.25 : 0 }, children: [_jsx(HoverTooltip, { title: label, placement: "top", children: _jsx(Typography, { className: "all-Font-Sizes", sx: {
                                color: "#101828",
                                fontWeight: 500,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                width: "100%",
                            }, children: label }) }), isCampaignCol && status && (_jsx(HoverTooltip, { title: `Status: ${status}`, placement: "top", children: _jsxs(Typography, { sx: {
                                fontSize: "12px !important",
                                color: "#101828",
                                fontWeight: 500,
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                width: "100%",
                                mt: "2px",
                                textTransform: "capitalize"
                            }, children: ["Status: ", status] }) })), isAdNameLevel && label && label !== "All" && label !== "-" && (_jsxs(Box, { component: "button", type: "button", onClick: (e) => {
                            e.stopPropagation();
                            handlePreviewOpen(row, label);
                        }, sx: {
                            border: "none",
                            backgroundColor: "transparent",
                            cursor: "pointer",
                            p: 0,
                            mt: 0.25,
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "4px",
                            color: "inherit",
                            lineHeight: 1,
                            width: "fit-content",
                            "&:hover": {
                                opacity: 0.75,
                                "& .preview-label": { textDecoration: "underline" },
                            },
                        }, children: [_jsx(Eye, { size: 13, style: { flexShrink: 0 } }), _jsx(Typography, { className: "preview-label", sx: {
                                    fontSize: "12px !important",
                                    fontWeight: 500,
                                    color: "inherit",
                                    lineHeight: 1,
                                }, children: "Preview" })] })), compareLabel && (_jsx(Typography
                    // `.reusable-table tbody tr td *` forces body size on
                    // every cell child, so the caption size is set here.
                    , { 
                        // `.reusable-table tbody tr td *` forces body size on
                        // every cell child, so the caption size is set here.
                        sx: {
                            fontSize: "var(--font-size-small) !important",
                            color: "#667085",
                            fontWeight: 500,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            mt: 0.25,
                        }, title: `Comparison: ${compareLabel}`, children: compareLabel }))] }));
        }
        const value = row.values[column.id];
        const compare = row.compare_values?.[column.id];
        return (_jsx(ComparisonCell
        // A comparison-only row (the compare range is longer than the
        // period) has no primary figure and so no change either.
        , { 
            // A comparison-only row (the compare range is longer than the
            // period) has no primary figure and so no change either.
            value: row.compare_only ? "-" : formatValue(value, definition.format), compare: compare != null ? formatValue(compare, definition.format) : null, change: compare != null && !row.compare_only ? percentChange(value, compare) : null, 
            // A day row in the tree is a subtotal — bold, like the reference.
            bold: row.level === "parent" }));
    };
    const renderSummaryRow = (_rows, column) => {
        const definition = report?.columns.find((c) => c.id === column.id);
        if (!report || !definition)
            return null;
        if (definition.is_dimension) {
            // Label only on the first dimension column.
            const first = report.columns.find((c) => c.is_dimension);
            if (first?.id !== column.id)
                return null;
            return (_jsx(Box, { sx: { display: "flex", flexDirection: "column", gap: 0.125 }, children: _jsx(Typography, { className: "all-Font-Sizes", sx: { fontWeight: 700, color: "#101828" }, children: "Total" }) }));
        }
        const total = report.totals[column.id];
        const compareTotal = report.compare_totals?.[column.id];
        return (_jsx(ComparisonCell, { value: formatValue(total, definition.format), compare: compareTotal != null ? formatValue(compareTotal, definition.format) : null, change: compareTotal != null ? percentChange(total, compareTotal) : null, bold: true }));
    };
    // ── Empty / not-yet-generated states ─────────────────────────────────
    if (loading) {
        return (_jsx(Box, { sx: {
                minHeight: 360,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
            }, children: _jsx(CommonLoader, { className: "table-loader" }) }));
    }
    if (!querySummary.isValid) {
        return (_jsx(EmptyState, { title: state.network_keys.length === 0
                ? "Start by turning on a network"
                : "Start by adding a metric", body: state.network_keys.length === 0
                ? "Pick the networks this report should read from in the Networks panel. Each one loads its predefined columns."
                : "Add the metrics you want as columns from the Metrics panel, then arrange them and use Generate & Save Report.", action: onAskAi && !disabled ? (_jsxs(Box, { sx: { display: "flex", flexDirection: "column", alignItems: "center", gap: 1 }, children: [_jsx(Typography, { className: "all-Font-Sizes-12", sx: { color: "#98A2B3" }, children: "or" }), _jsx(AppButton, { variant: "outlined", onClick: onAskAi, startIcon: _jsx(Sparkles, { size: 15 }), sx: {
                            height: 38,
                            px: 2,
                            textTransform: "none",
                            fontWeight: 600,
                            borderRadius: "8px",
                            borderColor: "#FFC8E9",
                            color: "#FF008E",
                            bgcolor: "#FFFAFD",
                            "&:hover": { borderColor: "#FF008E", bgcolor: "#FFF3FA" },
                        }, children: "Ask the G8A AI Agent to build it" })] })) : undefined }));
    }
    if (!report) {
        return (_jsx(Box, { sx: { display: "flex", flexDirection: "column", gap: 1.5 }, children: _jsx(EmptyState, { title: "Ready to generate", body: "Your selection is complete. Use Generate & Save Report in the controls panel to pull the data and preview it here." }) }));
    }
    // ── One table per network (Campaign / Age across networks) ──────────
    // Same config, data kept apart: each network's table is this same preview
    // on its own report — its own sort, expand state and totals row.
    if (report.tables?.length) {
        return (_jsx(Box, { sx: { display: "flex", flexDirection: "column", gap: 2 }, children: report.tables.map((table) => (_jsx(ReportPreview, { state: state, report: table.report, loading: false, stale: stale, querySummary: querySummary, onGenerate: onGenerate, disabled: disabled, clientName: clientName, clientId: clientId, onEditCustomMetric: onEditCustomMetric, 
                // The card's title: the network, with the same brand
                // artwork as the Networks panel.
                title: _jsxs(Box, { sx: { display: "flex", alignItems: "center", gap: 1 }, children: [_jsx(GroupIcon, { group: toBuilderNetworkKey(table.network), size: 20 }), _jsx(Typography, { className: "all-Font-Sizes-16", sx: { fontWeight: 600, color: "#101828" }, children: table.label })] }) }, table.network))) }));
    }
    // ── Generated ────────────────────────────────────────────────────────
    // "Afina (Day › Campaign)" — the client, then the levels the table is
    // really cut by (its label columns), so a skipped Age is not claimed.
    const dimensionLabels = report.columns.filter((c) => c.is_dimension).map((c) => c.label);
    const cardTitle = `${clientName || "Report"}${dimensionLabels.length ? ` (${dimensionLabels.join(" › ")})` : ""}`;
    return (_jsxs(Box, { sx: { display: "flex", flexDirection: "column", gap: 1.5 }, children: [_jsxs(Paper, { elevation: 0, sx: { border: "1px solid #E4E7EC", borderRadius: "8px", p: 1, overflow: "hidden" }, children: [_jsx(TableToolbar, { title: title, showFullScreen: false }), _jsx(CommonTable, { columns: columns, rows: rows, renderCell: renderCell, sortConfig: sortConfig, onSortChange: (columnId, direction) => setSortConfig(direction ? { columnId, direction } : null), emptyMessage: "No rows matched the filters on this selection.", stickyHeader: true, expandTrigger: "none", maxHeight: "520px", 
                        // A table with no label column is one row of period totals
                        // already (a network the picked breakdown does not apply to).
                        enableSummaryRow: report.columns.some((c) => c.is_dimension), renderSummaryRow: renderSummaryRow, summaryRowPosition: "bottom", 
                        // Day rows of the tree sit on grey, their children on white.
                        // Days sit on grey, campaigns / ad sets on a lighter grey,
                        // the leaves on white — so each level reads as a band.
                        rowSx: (row) => row.level !== "parent"
                            ? undefined
                            : { "& td": { bgcolor: `${(row.depth || 1) === 1 ? "#F2F4F7" : "#F9FAFB"} !important` } }, pinnedColumns: {
                            left: report.columns.filter((c) => c.is_dimension).map((c) => c.id),
                            right: [],
                        } })] }), _jsx(AdPreview, { open: previewOpen, onClose: handleClosePreview, previewRow: previewRow, previewFormat: previewFormat, previewFormatOptions: previewFormatOptions, onPreviewFormatChange: handlePreviewFormatChange, loading: previewData.loading, error: previewData.error, html: previewData.html, previewUrl: previewData.url })] }));
};
export default ReportPreview;
